import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, FileText, Palette, SlidersHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { EvidenceTag } from "@/components/onboarding/KitBuilder";
import { api, errMsg, streamMessage } from "@/lib/api";
import { MessageBubble } from "@/components/MessageBubble";
import { Composer } from "@/components/Composer";
import { ReadinessBadge } from "@/components/Badges";

const LOVE = [["L", "Layers"], ["O", "Optics"], ["V", "Variables"], ["E", "Evaluation"]];

function SidePanel({ kit }) {
  return (
    <aside className="hidden w-80 shrink-0 space-y-5 overflow-y-auto border-r border-[#E6DBCF] bg-[#F6F1EA] p-6 xl:block">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#A88B7D]">Framework</p>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {LOVE.map(([l, n]) => (
            <div key={l} className="rounded-xl bg-[#FDFBF7] p-2 text-center">
              <p className="font-serif text-2xl italic text-[#C5A059]">{l}</p>
              <p className="text-[9px] uppercase tracking-wider text-[#6E5549]">{n}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl bg-[#FDFBF7] p-4">
        <p className="flex items-center gap-2 text-sm font-semibold text-[#2C1810]"><Camera size={15} /> Photo guidance</p>
        <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-[#6E5549]">
          <li>Consented, de-identified, cropped photos only.</li>
          <li>Consistent lighting, no filters, multiple angles.</li>
          <li>Pigment label photos help verify CI lists.</li>
          <li>Photos never determine undertone, depth, chemistry or readiness.</li>
        </ul>
      </div>
      <div className="rounded-2xl bg-[#FDFBF7] p-4" data-testid="consult-kit-panel">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#2C1810]"><Palette size={15} /> Your kit</p>
          <Link to="/kit" className="text-[10px] uppercase tracking-[0.2em] text-[#9C6218] hover:underline" data-testid="consult-edit-kit-link">Edit</Link>
        </div>
        <ul className="mt-2 space-y-1.5">
          {kit.map((k) => (
            <li key={k.id} className="text-xs text-[#4A3B32]">
              {k.brand} · {k.shade || k.line}
              {!k.pigment_id && <span className="ml-1 text-[9px] font-semibold uppercase text-[#8C2A2A]">unverified</span>}
            </li>
          ))}
          {!kit.length && <li className="text-xs italic text-[#A88B7D]">No pigments saved — tell Ink Genius what you have.</li>}
        </ul>
      </div>
    </aside>
  );
}

const QUICK = { brows: ["New Brow Procedure", "Previously Tattooed Brows"], lips: ["Virgin Lips", "Previously Tattooed Lips"] };

function KitDialog({ open, onOpenChange, kit, selected, onSave }) {
  const [ids, setIds] = useState(selected);
  const [busy, setBusy] = useState(false);
  useEffect(() => setIds(selected), [selected, open]);
  const save = async () => {
    setBusy(true);
    const ok = await onSave(ids);
    setBusy(false);
    if (ok) onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto bg-[#FDFBF7]" data-testid="consult-kit-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl">Pigments for this consultation</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6E5549]">Your saved kit is included automatically. Untick anything that isn't available for this case.</p>
        <ul className="mt-2 space-y-2">
          {kit.map((k) => (
            <li key={k.id}>
              <label className="flex min-h-[52px] cursor-pointer items-center gap-3 rounded-xl border border-[#E6DBCF] bg-white/70 px-4 py-2">
                <Checkbox data-testid={`consult-kit-toggle-${k.id}`} checked={ids.includes(k.id)}
                  onCheckedChange={(v) => setIds(v ? [...ids, k.id] : ids.filter((x) => x !== k.id))} className="h-5 w-5 data-[state=checked]:bg-[#2C1810]" />
                <span className="min-w-0 flex-1 truncate text-sm text-[#2C1810]">{k.brand} · {k.shade || k.line}</span>
                <EvidenceTag status={k.pigment?.verification_status} userAdded={!k.pigment_id} />
              </label>
            </li>
          ))}
          {!kit.length && <li className="text-sm italic text-[#A88B7D]">Your kit is empty — add pigments in My Kit, or describe them in the chat.</li>}
        </ul>
        <button onClick={save} disabled={busy} data-testid="consult-kit-save-btn" className="mt-4 min-h-[48px] w-full rounded-full bg-[#2C1810] text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:opacity-50">
          {busy ? "Saving…" : `Use ${ids.length} pigment${ids.length === 1 ? "" : "s"}`}
        </button>
      </DialogContent>
    </Dialog>
  );
}

export default function ConsultationPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [messages, setMessages] = useState([]);
  const [consult, setConsult] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const lastUserId = useRef(null);
  const bottom = useRef(null);
  const { data: kit = [] } = useQuery({ queryKey: ["kit"], queryFn: async () => (await api.get("/kit")).data });

  useEffect(() => {
    api.get(`/consultations/${id}`).then(({ data }) => {
      setConsult(data.consultation);
      setMessages(data.messages);
    }).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  const selectedIds = consult?.kit_item_ids ?? kit.map((k) => k.id);
  const activeKit = kit.filter((k) => selectedIds.includes(k.id));
  const saveKit = async (ids) => {
    try {
      const { data } = await api.put(`/consultations/${id}/kit`, { kit_item_ids: ids });
      setConsult(data);
      toast.success("Pigments for this consultation updated");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const send = async (text, image_ids) => {
    setStreaming(true);
    const tempUser = { id: `tmp-u-${Date.now()}`, role: "user", content: text, image_ids };
    const tempAsst = { id: "streaming", role: "assistant", content: "" };
    setMessages((m) => [...m, tempUser, tempAsst]);
    try {
      await streamMessage(id, { text, image_ids }, (ev) => {
        if (ev.type === "user") {
          lastUserId.current = ev.message.id;
          setMessages((m) => m.map((x) => (x.id === tempUser.id ? ev.message : x)));
        }
        if (ev.type === "delta") setMessages((m) => m.map((x) => (x.id === "streaming" ? { ...x, content: x.content + ev.content } : x)));
        if (ev.type === "done") {
          setMessages((m) => m.map((x) => (x.id === "streaming" ? ev.message : x)));
          setConsult((c) => ({ ...c, title: ev.title, readiness: ev.message.recommendation?.readiness?.status || c?.readiness }));
        }
        if (ev.type === "error") {
          toast.error(ev.detail);
          setMessages((m) => (m.some((x) => x.id === "streaming" && x.content) ? m : m.filter((x) => x.id !== "streaming" && x.id !== tempUser.id && x.id !== lastUserId.current)));
        }
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMessages((m) => m.filter((x) => x.id !== "streaming" || x.content));
      setStreaming(false);
      qc.invalidateQueries({ queryKey: ["consultations"] });
    }
  };

  return (
    <div className="flex h-[calc(100dvh-200px)] lg:h-screen">
      <SidePanel kit={activeKit} />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-[#E6DBCF] bg-[#FDFBF7]/85 px-6 py-4 backdrop-blur-xl">
          <div className="min-w-0 flex-1">
            <p className="truncate whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.25em] text-[#A88B7D]" data-testid="consult-area-label">{consult?.area === "lips" ? "Lip blush" : "Brows"} · L.O.V.E. consultation</p>
            <h1 className="truncate font-serif text-2xl font-semibold text-[#2C1810]" data-testid="consult-title">{consult?.title || "…"}</h1>
          </div>
          <span className="hidden sm:inline-flex"><ReadinessBadge status={consult?.readiness} testId="consult-readiness-badge" /></span>
          {consult?.readiness && (
            <Link to={`/reports/${id}`} data-testid="consult-view-report-btn" aria-label="View report" className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-full bg-[#2C1810] px-4 text-xs font-semibold text-[#FDFBF7] hover:bg-[#3D2317]">
              <FileText size={14} /> <span className="hidden sm:inline">Report</span>
            </Link>
          )}
          <button onClick={() => setKitOpen(true)} data-testid="consult-kit-btn" className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[#E6DBCF] px-4 text-xs font-semibold text-[#2C1810] hover:bg-[#F6F1EA]">
            <SlidersHorizontal size={14} /> Kit · <span data-testid="consult-kit-count">{activeKit.length}</span>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-8 sm:px-8" data-testid="chat-messages">
          <div className="mx-auto max-w-5xl space-y-6">
            {messages.map((m, i) => (
              <MessageBubble key={m.id} m={m} streaming={streaming && m.id === "streaming"}
                onChoose={i === messages.length - 1 && m.role === "assistant" && !streaming ? (t) => send(t, []) : undefined} />
            ))}
            {messages.length === 1 && !streaming && (
              <div className="flex flex-wrap gap-2 pl-11" data-testid="consult-quick-replies">
                {QUICK[consult?.area || "brows"].map((q) => (
                  <button key={q} onClick={() => send(q, [])} data-testid={`quick-reply-${q.toLowerCase().replace(/ /g, "-")}`}
                    className="min-h-[48px] rounded-full border border-[#2C1810] px-5 text-sm font-semibold text-[#2C1810] transition-colors hover:bg-[#2C1810] hover:text-[#FDFBF7]">
                    {q}
                  </button>
                ))}
              </div>
            )}
            {streaming && messages[messages.length - 1]?.content === "" && (
              <div className="flex items-center gap-2 pl-11 text-xs text-[#A88B7D]" data-testid="assistant-typing">
                {[0, 1, 2].map((i) => <span key={i} className="ig-dot h-1.5 w-1.5 rounded-full bg-[#C5A059]" style={{ animationDelay: `${i * 0.2}s` }} />)}
                Ink Genius is considering…
              </div>
            )}
            <div ref={bottom} />
          </div>
        </div>
        <KitDialog open={kitOpen} onOpenChange={setKitOpen} kit={kit} selected={selectedIds} onSave={saveKit} />
        <div className="px-4 pb-5 sm:px-8">
          <div className="mx-auto max-w-5xl">
            <Composer onSend={send} disabled={streaming || !consult} />
            <p className="mt-2 text-center text-[10px] text-[#A88B7D]">Educational decision support only — no clinical clearance. The artist makes the final decision.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
